import { useMemo } from 'react'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import useOfferList from 'hooks/Offers/useOfferList'
import { TOP_N, HOTEL_OFFER_TYPES, hasTopOffersChanged } from 'hooks/salesRankingUtils'

interface UseSalesAndMpvRankingResult {
  experimentVariant: string | undefined
  rankingComparisonPending: boolean
}

// Determines whether to activate the search_sales_mpv_ranking experiment.
// We only want to trigger the experiment when the user would actually see different
// results — so we call the light offer-list endpoint with and without the mpvSalesRanking
// rollout and compare the top 3 offers. If there's a difference, the experiment can be
// activated. Returns the Optimizely variant when triggered, or undefined if it can't be.
function useSalesAndMpvRanking(destinationId: string | undefined): UseSalesAndMpvRankingResult {
  const shouldCompare = !!destinationId

  const baseFilters = useMemo((): App.OfferListFilters | undefined => {
    if (!shouldCompare) return undefined
    return {
      destinationId,
      offerTypes: HOTEL_OFFER_TYPES,
      limit: TOP_N,
    }
  }, [shouldCompare, destinationId])

  const filtersWithRollout = useMemo((): App.OfferListFilters | undefined => {
    if (!baseFilters) return undefined
    return {
      ...baseFilters,
      rollout: ['mpvSalesRanking'],
    }
  }, [baseFilters])

  const withRolloutList = useOfferList(filtersWithRollout, { disabled: !shouldCompare })
  const withoutRolloutList = useOfferList(baseFilters, { disabled: !shouldCompare })

  const rankingComparisonPending = shouldCompare && (withRolloutList.fetching || withoutRolloutList.fetching)

  const rankingsDiffer = useMemo(() => {
    if (!shouldCompare || rankingComparisonPending) return false

    return hasTopOffersChanged(withRolloutList.offerIds, withoutRolloutList.offerIds)
  }, [shouldCompare, rankingComparisonPending, withRolloutList.offerIds, withoutRolloutList.offerIds])

  const canActivate = shouldCompare && !rankingComparisonPending && rankingsDiffer

  const experimentVariant = useOptimizelyExperiment(OptimizelyExperiments.searchSalesMpvRanking, canActivate)

  return { experimentVariant, rankingComparisonPending }
}

export default useSalesAndMpvRanking
